import { IDoctor, InstitutionType } from '@alberta/konexi-shared';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AttachmentViewModel } from 'src/app/business/attachment/view-models/attachment-view-model';
import { InfoBoxType } from 'src/app/shared/components/common/info-box/info-box.component';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { AuthService } from 'src/app/shared/services/auth.service';
import { InstitutionAttributeService } from 'src/app/shared/services/institution-attribute.service';
import { PreviewModalService } from 'src/app/shared/services/preview-modal.service';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { InstitutionAttributeConfigService } from 'src/app/shared/services/tenant-config/institution-attribute-config.service';
import { TenantConfigService } from 'src/app/shared/services/tenant-config/tenant-config.service';

import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { validateEmail } from '@common/email/email-validator';
import { DoctorDB } from '@common/repository/databases';
import { PrescriptionRequestAttachmentsComponent } from './prescription-request-attachments/prescription-request-attachments';

@Component({
  selector: 'swodoc-node-prescription-data',
  templateUrl: 'swodoc-node-prescription-data.html',
  styleUrls: ['swodoc-node-prescription-data.scss'],
  providers: [InstitutionAttributeConfigService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwodocNodePrescriptionData implements OnInit {
  @Input()
  node: any;
  get shouldShowCorrespondenceAddress() {
    return this.prescriptionData?.correspondenceType !== 'Drucken';
  }

  get prescriptionData() {
    return this.node?.responses[0]?.value;
  }

  set prescriptionData(value) {
    this.node.responses[0].value = value;
  }
  public correspondenceTypes: SelectionItem[] = [];
  public doctor: IDoctor;
  public correspondenceNotVerified: boolean;
  public InfoBoxType = InfoBoxType;
  public attachmentTypes: number[] = [];
  public selectedAttachments: AttachmentViewModel[] = [];
  public correspondenceAddressForm = new FormGroup({
    to: new FormControl(undefined),
  });
  private _userEmail: string;

  constructor(
    private _institutionAttributeConfigService: InstitutionAttributeConfigService,
    private _institutionAttributeService: InstitutionAttributeService,
    private _queryService: QueryService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _auth: AuthService,
    private _tenantConfigService: TenantConfigService,
    private _modalController: ModalController,
    private _previewService: PreviewModalService
  ) {}

  async ngOnInit() {
    this._userEmail = await this._auth.getUserEmail();
    this.node.mandatory = true;
    await this.doctorChanged(this.prescriptionData.doctorId);
    const institutionAttributeConfig = await this._institutionAttributeConfigService.getAttributesForInstitutionType(
      InstitutionType.Doctor
    );
    if (institutionAttributeConfig?.length) {
      const correspondenceConfig = institutionAttributeConfig.find(attribute => attribute.name === 'Korrespondenzart');
      if (correspondenceConfig?.values?.length) {
        correspondenceConfig.values.forEach(correspondenceType => {
          this.correspondenceTypes.push(new SelectionItem(correspondenceType, correspondenceType));
        });
      }
    }
    if (!Capacitor.isNativePlatform()) {
      this.correspondenceTypes.push(new SelectionItem('Drucken', 'Drucken'));
    }
    this.attachmentTypes = await this._tenantConfigService.getPrescriptionRequestAttachmentTypes();
    if (this.prescriptionData.attachments?.length) {
      this.selectedAttachments = this.prescriptionData.attachments;
    }
    // this.correspondenceAddressForm.get('to')?.valueChanges.subscribe(value => {
    //   this.prescriptionData.to = value;
    // });
    // if (this.prescriptionData.to) {
    //   this.correspondenceAddressForm.get('to').setValue(this.prescriptionData.to, { emitEvent: false });
    // }
    this._changeDetectorRef.markForCheck();
  }

  async ngOnChanges() {
    // we need to manually trigger the validators here again after navigating back to this page
    await this.reValidateCorrespondenceAddress();
  }

  private async reValidateCorrespondenceAddress() {
    if (!this.prescriptionData?.correspondenceType) {
      return;
    }
    switch (this.prescriptionData.correspondenceType) {
      case 'Fax':
        this.correspondenceAddressForm.controls.to.setValidators(Validators.required);
        break;
      case 'E-Mail an mich':
        this.correspondenceAddressForm.controls.to.setValidators([Validators.required, validateEmail]);
        break;
      case 'Drucken':
        // no validation needed
        this.correspondenceAddressForm.controls.to.clearValidators();
        this.correspondenceAddressForm.controls.to.setValidators(null);
        break;

      default:
        this.correspondenceAddressForm.controls.to.setValidators([Validators.required, validateEmail]);
        break;
    }
    this.correspondenceAddressForm.controls.to.updateValueAndValidity();
    this.correspondenceAddressForm.controls.to.markAsTouched();
  }
  public async doctorChanged(doctorId: string) {
    this.doctor = await this._queryService.get<IDoctor>(doctorId, DoctorDB);
    const correspondenceType = this.prescriptionData.correspondenceType
      ? this.prescriptionData.correspondenceType
      : await this._institutionAttributeService.getCorrespondenceTypeForInstitutionId(doctorId);
    const isNewDoctor = this.prescriptionData.previousDoctorId !== doctorId;
    if (this.prescriptionData.correspondenceType !== correspondenceType || isNewDoctor) {
      this.prescriptionData.correspondenceType = correspondenceType;
      this.correspondenceTypeChanged(correspondenceType);
    }
    this.prescriptionData.previousDoctorId = doctorId;
  }

  public correspondenceTypeChanged(correspondenceType: string) {
    let to: string;
    this.correspondenceNotVerified = false;
    switch (correspondenceType) {
      case 'Fax':
        this.correspondenceAddressForm.controls.to.setValidators(Validators.required);
        if (this.doctor?.draft?.faxPrescriptionRequest) {
          this.correspondenceNotVerified = true;
          to = this.doctor.draft.faxPrescriptionRequest;
        } else if (this.doctor?.faxPrescriptionRequest) {
          to = this.doctor.faxPrescriptionRequest;
        } else if (this.doctor?.draft?.fax) {
          this.correspondenceNotVerified = true;
          to = this.doctor.draft.fax;
        } else if (this.doctor?.fax) {
          to = this.doctor.fax;
        }
        break;
      case 'E-Mail an mich':
        this.correspondenceAddressForm.controls.to.setValidators([Validators.required, validateEmail]);
        to = this._userEmail;
        break;
      case 'Drucken':
        this.correspondenceAddressForm.controls.to.clearValidators();
        this.correspondenceAddressForm.controls.to.setValue(undefined);
        to = undefined;
        break;
      default:
        this.correspondenceAddressForm.controls.to.setValidators([Validators.required, validateEmail]);
        if (this.doctor?.draft?.emailPrescriptionRequest) {
          this.correspondenceNotVerified = true;
          to = this.doctor.draft.emailPrescriptionRequest;
        } else if (this.doctor?.emailPrescriptionRequest) {
          to = this.doctor.emailPrescriptionRequest;
        } else if (this.doctor?.draft?.email) {
          this.correspondenceNotVerified = true;
          to = this.doctor.draft.email;
        } else if (this.doctor?.email) {
          to = this.doctor.email;
        }
        break;
    }
    this.prescriptionData.to = to;
    this.correspondenceAddressForm.controls.to.updateValueAndValidity();
    this._changeDetectorRef.markForCheck();
  }

  public async addAttachments() {
    const addAttachmentsModal = await this._modalController.create({
      component: PrescriptionRequestAttachmentsComponent,
      backdropDismiss: false,
      componentProps: {
        attachmentTypes: this.attachmentTypes,
        patientId: this.prescriptionData.patientId,
      },
      cssClass: 'fullPageModal',
    });
    await addAttachmentsModal.present();
    const modalResult = await addAttachmentsModal.onDidDismiss();
    if (modalResult?.data?.length) {
      for (const attachment of modalResult.data) {
        // only push if not already selected
        if (!this.selectedAttachments?.find(selectedAttachment => selectedAttachment._id === attachment._id)) {
          this.selectedAttachments.push(attachment);
          this._changeDetectorRef.markForCheck();
        }
      }
    }
    this.updatePrescriptionDataAttachments();
  }

  public removeAttachment(attachment: AttachmentViewModel) {
    this.selectedAttachments = this.selectedAttachments.filter(
      selectedAttachment => selectedAttachment._id !== attachment._id
    );
    this.updatePrescriptionDataAttachments();
    this._changeDetectorRef.markForCheck();
  }

  public async previewAttachment(attachment: AttachmentViewModel) {
    await this._previewService.previewAttachment(attachment);
  }

  private updatePrescriptionDataAttachments() {
    this.prescriptionData.attachments = this.selectedAttachments.map(selectedAttachment => ({
      _id: selectedAttachment._id,
      filename: selectedAttachment.filename,
      contentType: selectedAttachment.contentType,
      createdAt: selectedAttachment?.metadata?.createdAt || selectedAttachment.uploadDate,
    }));
  }
}
